import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, Card, Spin } from 'antd';
import Login from 'screens/Login';
import AuthService from 'utils/libs/auth/AuthService';
import { loadWatermarkedPhoto, loadOriginalPhoto, originalPhotoEndpoint, photoOwner, photoOwnerKey, persistedPhotoOwnerKey, watermarkedPhotoEndpoint, WATERMARKED_PHOTO_ROUTE, ORIGINAL_PHOTO_ROUTE } from 'services/watermarkedPhoto';

const auth = new AuthService();
const authenticated = () => { try { return auth.loggedIn(); } catch (_) { return false; } };
const messages = {
  PHOTO_WATERMARK_PENDING: 'La foto se está preparando. Vuelva a intentarlo en unos momentos.',
  PHOTO_WATERMARK_INCOMPLETE: 'La foto todavía necesita información antes de estar disponible.',
  PHOTO_WATERMARK_STALE: 'La foto fue actualizada. Abra el enlace del informe actualizado.',
  PHOTO_WATERMARK_FAILED: 'No se pudo preparar la foto. Contacte al administrador.',
};
const unavailable = 'La foto no está disponible para este perfil. Compruebe su sesión y la organización seleccionada.';

// Keep the durable route mounted through the existing login flow; no external return URL.
export default function WatermarkedPhoto({ match, original = false }) {
  const login = useSelector(state => state.login);
  const owner = photoOwner(login.profile), ownerKey = photoOwnerKey(owner), persistedOwnerKey = persistedPhotoOwnerKey();
  const alignedProfile = !!ownerKey && ownerKey === persistedOwnerKey;
  const token = localStorage.getItem('synapse_token'), logged = login.logged !== false && authenticated();
  const { nativePhotoId, version } = match.params;
  const durablePath = original ? `/photos/${nativePhotoId}/original` : `/photos/${nativePhotoId}/watermarked/${version}`;
  const [loaded, setLoaded] = useState(null), [error, setError] = useState(''), [attempt, setAttempt] = useState(0);
  const [sessionTick, setSessionTick] = useState(0);
  const currentOwner = useRef();
  const activeRequest = useRef();
  currentOwner.current = alignedProfile ? ownerKey : null;
  let valid = true;
  try { if (original) originalPhotoEndpoint(nativePhotoId); else watermarkedPhotoEndpoint(nativePhotoId, version); } catch (_) { valid = false; }

  useEffect(() => {
    const changed = () => {
      const request = activeRequest.current;
      if (request && !request.isCurrent()) request.cancel();
      setSessionTick(value => value + 1);
    };
    // Redux handles same-tab login/profile/logout; storage handles changes from another tab.
    window.addEventListener('storage', changed);
    const timer = setInterval(changed, 1000); // Also clear the displayed lease when the local session expires.
    return () => { clearInterval(timer); window.removeEventListener('storage', changed); };
  }, []);

  useEffect(() => {
    setLoaded(null); setError('');
    if (!logged || !alignedProfile || !valid) return undefined;
    let lease, active = true;
    try {
      lease = (original ? loadOriginalPhoto : loadWatermarkedPhoto)({ photoId: nativePhotoId, version, owner,
        isCurrent: () => currentOwner.current === ownerKey && persistedPhotoOwnerKey() === ownerKey && authenticated() });
      activeRequest.current = lease;
      lease.done.then(url => {
        if (active && lease.isCurrent()) setLoaded({ url, lease, ownerKey, nativePhotoId, version });
      }).catch(failure => {
        if (active && authenticated() && currentOwner.current === ownerKey && persistedPhotoOwnerKey() === ownerKey) setError(messages[failure.code] || unavailable);
      });
    } catch (_) { setError(unavailable); }
    return () => { active = false; if (activeRequest.current === lease) activeRequest.current = null; if (lease) lease.cancel(); };
  }, [nativePhotoId, version, original, ownerKey, persistedOwnerKey, token, logged, attempt, valid, alignedProfile]);

  // Reading sessionTick triggers a render; it is deliberately not a download dependency.
  void sessionTick;
  if (!logged) return <Login />;
  const visible = alignedProfile && loaded && loaded.ownerKey === ownerKey && loaded.nativePhotoId === nativePhotoId &&
    loaded.version === version && loaded.lease.isCurrent();
  return <Card title="Foto del informe" style={{ margin: 24 }}>
    {!valid ? <Alert type="warning" message="El enlace de la foto no es válido." /> : <>
      {!alignedProfile && <Alert type="info" message="Esperando el perfil seleccionado. Actualice la página si cambió de perfil en otra pestaña." />}
      {!alignedProfile && <p><a href={durablePath}>Actualizar página</a></p>}
      {error && <Alert role="alert" type="warning" message={error} />}
      {alignedProfile && !error && !visible && <Spin tip="Preparando foto…" />}
      {visible && <>
        <img src={loaded.url} alt={original ? 'Foto original del informe' : 'Foto del informe con marca de agua'} style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
          onError={() => { loaded.lease.cancel(); setLoaded(null); setError(unavailable); }} />
        <p><a href={loaded.url} download={original ? `foto-${nativePhotoId}.${loaded.lease.contentType === 'image/png' ? 'png' : 'jpg'}` : `foto-${nativePhotoId}-${version.slice(0, 12)}.jpg`}>Descargar foto</a></p>
      </>}
      {alignedProfile && <Button onClick={() => setAttempt(value => value + 1)}>Volver a cargar</Button>}
    </>}
    <p><a href="/">Ir a la aplicación para seleccionar organización</a></p>
  </Card>;
}

export const watermarkedPhotoRoute = { exact: true, path: WATERMARKED_PHOTO_ROUTE, component: WatermarkedPhoto };
export const originalPhotoRoute = { exact: true, path: ORIGINAL_PHOTO_ROUTE,
  component: props => <WatermarkedPhoto {...props} original /> };
